import { FC, ReactNode } from "react"

import { classNames } from "../../../utils"

interface Props {
  title: string
  /** Plain-language answer to "what am I looking at?", shown under the title. */
  description?: string
  /** Tabs, selects or a legend that scope this panel only. */
  actions?: ReactNode
  /** Summary figures pinned under the plot. */
  footer?: ReactNode
  /**
   * Tables and maps run edge to edge. A prop rather than a className override:
   * the classNames util doesn't resolve conflicting Tailwind utilities, so
   * "px-0" passed in would fight the default padding instead of replacing it.
   */
  flush?: boolean
  children: ReactNode
}

/**
 * The shell every Stats panel sits in. Flat by design — panels aren't
 * clickable, so they carry a border and no resting or hover shadow. It doesn't
 * clip its contents, so a dropdown or date picker in the header can overhang.
 */
const StatsPanel: FC<Props> = ({
  title,
  description,
  actions,
  footer,
  flush = false,
  children
}) => (
  <section className="flex min-w-0 flex-1 flex-col rounded-xl border border-slate-200 bg-white">
    <header className="flex flex-wrap items-start justify-between gap-x-4 gap-y-3 px-5 pt-4 md:px-6 md:pt-5">
      <div className="min-w-0 flex-1 basis-48">
        <h3 className="text-sm font-semibold leading-5 text-base-dark">
          {title}
        </h3>
        {description && (
          <p className="mt-0.5 text-xs leading-4 text-slate-500">
            {description}
          </p>
        )}
      </div>
      {actions && (
        <div className="flex min-w-0 max-w-full flex-wrap items-center gap-2">
          {actions}
        </div>
      )}
    </header>

    <div
      className={classNames(
        "flex min-w-0 flex-1 flex-col pb-5 pt-4 md:pb-6",
        !flush && "px-5 md:px-6"
      )}
    >
      {children}
    </div>

    {footer && (
      <footer className="rounded-b-xl border-t border-slate-100 bg-slate-50/70 px-5 py-3 md:px-6">
        {footer}
      </footer>
    )}
  </section>
)

export default StatsPanel
